import { useMemo, type PointerEvent as ReactPointerEvent } from 'react';
import { useRangeDrag } from '../../hooks/useRangeDrag';

/** One zone cell of a floor row. */
export interface IFloorZone {
  id: string;
  name: string;
}

/** One floor row: the floor, and the zone cells its track shows. */
export interface IFloorZoneRow<Z extends IFloorZone = IFloorZone> {
  id: string;
  name: string;
  zones: readonly Z[];
}

export interface IZoneSweep<TMeta> {
  /** A press-drag is in flight. */
  sweeping: boolean;
  /**
   * What the sweep in flight paints over this zone — the meta its press
   * captured — or `undefined` when the zone lies outside the swept range, is
   * not on the grid at all, or nothing is being swept.
   */
  sweptMeta: (zoneId: string) => TMeta | undefined;
  /** Anchor a sweep on the cell at `index` in the flattened grid order. */
  start: (e: ReactPointerEvent<HTMLElement>, index: number, meta: TMeta) => void;
  /** Carry the sweep in flight to the cell at `index`. */
  extend: (index: number) => void;
  /** True exactly once for the click that trails a travelled sweep — read and consumed. */
  shouldSuppressClick: () => boolean;
}

/**
 * The press-drag SWEEP over a floor/zone grid: `useRangeDrag` bound to the
 * grid's flattened cell order — floor after floor, zone after zone — so a
 * range carries on into the next floor rather than stopping at a row's end.
 *
 * Owned by the surface, not by `FloorZoneGrid`, because the preview reaches
 * beyond the grid: a floor's count and the progress meter both read what the
 * sweep in flight is about to change.
 *
 * A press that never travelled is a TAP, not a sweep: it commits a one-cell
 * range, and the browser then fires a click on that same cell. Only the click
 * may act on it — acting on both would apply the cell twice — so `onSweep`
 * hears only travelled ranges and the tap is left to the grid's click.
 */
export function useZoneSweep<TMeta>(
  rows: readonly IFloorZoneRow[],
  onSweep: (zoneIds: string[], meta: TMeta) => void,
): IZoneSweep<TMeta> {
  const { orderedIds, indexById } = useMemo(() => {
    const ids = rows.flatMap((row) => row.zones.map((zone) => zone.id));
    return { orderedIds: ids, indexById: new Map(ids.map((id, index) => [id, index] as const)) };
  }, [rows]);
  const { stroke, startStroke, extendStroke, shouldSuppressClick } = useRangeDrag<TMeta>({
    onCommit: (lo, hi, meta) => {
      if (lo !== hi) onSweep(orderedIds.slice(lo, hi + 1), meta);
    },
  });

  const sweptMeta = (zoneId: string): TMeta | undefined => {
    const index = indexById.get(zoneId);
    if (!stroke || index === undefined) return undefined;
    const lo = Math.min(stroke.anchor, stroke.current);
    const hi = Math.max(stroke.anchor, stroke.current);
    return index >= lo && index <= hi ? stroke.meta : undefined;
  };

  return { sweeping: stroke !== null, sweptMeta, start: startStroke, extend: extendStroke, shouldSuppressClick };
}
